import React, { Children, cloneElement, isValidElement } from "react";

import { pressPoint } from "./glass";
import renderIcon from "./renderIcon";
import { cornerClass, joinedCorners, useGridColumns } from "./joined";

const cx = (...a) => a.filter(Boolean).join(" ");

/**
 * ReservationCardUI — caseta unei lecții (rezervări) într-o listă: data și ora sus, dedesubt
 * rândurile mici (instructorul, sectorul · cutia), în culoarea rezervării. Numărul lecției stă
 * absolut în colțul din dreapta-sus, ca o pastilă de sticlă (Liquid Glass: translucidă, cu
 * blur). Caseta are marginea de sticlă și lumina de la apăsare. Stilurile:
 * scss/UI/_reservationCardUI.scss.
 *
 * date · time: rândul de sus („01.10.2026, 07:00”, ora în accent)
 * lines: rândurile de dedesubt (text sau element) · flags: semnele de la capătul ultimului rând
 * number: numărul lecției (în colț) · current: lecția din care s-a deschis (inel accent)
 * cancelled: punctul roșu (anulată) · color: fundalul (culoarea rezervării, CSS)
 * tone: success | danger | info — fundalul și textul după rezultat (examenele: promovat, respins,
 *    în desfășurare), în locul lui color
 * action: { icon, label, onClick, busy, disabled } — butonul din colț, în locul numărului: doar
 *    iconița, fără fundal (descărcarea unui document); label e numele pentru cititor și titlul
 * onOpen: clic / Enter / Spațiu · title · className
 * corners: colțurile de la marginea grupului ({ tl, tr, bl, br }) — le dă ReservationCardUI.List
 *
 * ReservationCardUI.List — casetele lipite (3px), ca InputUI.Group: grila de 2 coloane (1 pe
 * ecrane înguste), doar colțurile de la marginea grupului rotunjite (24px), restul 8px; ultima
 * casetă fără pereche ia tot rândul. columns: câte coloane (implicit 2).
 */
function ReservationCardUI({
   date,
   time,
   lines = [],
   flags = null,
   number,
   current = false,
   cancelled = false,
   color,
   tone,
   action = null,
   onOpen,
   title,
   corners,
   className = "",
}) {
   const interactive = typeof onOpen === "function";
   const rows = (Array.isArray(lines) ? lines : [lines]).filter((l) => l !== null && l !== undefined && l !== false && l !== "");
   return (
      <article
         className={cx(
            "reservationCardUI",
            interactive && "is-interactive",
            current && "is-current",
            cancelled && "is-cancelled",
            tone && `is-${tone}`,
            action && "has-action",
            cornerClass(corners),
            className,
         )}
         style={color ? { "--reservationCardUI-bg": color } : undefined}
         title={title}
         onClick={interactive ? onOpen : undefined}
         onPointerDown={interactive ? pressPoint : undefined}
         role={interactive ? "button" : undefined}
         tabIndex={interactive ? 0 : undefined}
         onKeyDown={
            interactive
               ? (e) => {
                    if (e.key !== "Enter" && e.key !== " ") return;
                    e.preventDefault();
                    onOpen(e);
                 }
               : undefined
         }
      >
         {action ? (
            <button
               type="button"
               className={cx("reservationCardUI__action", action.busy && "is-busy")}
               title={action.busy ? "Se descarcă…" : action.label}
               aria-label={action.label}
               disabled={action.disabled || action.busy}
               onPointerDown={(e) => {
                  e.stopPropagation();
                  pressPoint(e);
               }}
               onClick={(e) => {
                  e.stopPropagation();
                  action.onClick?.(e);
               }}
            >
               {renderIcon(action.icon || "download", "reservationCardUI__actionIcon")}
            </button>
         ) : number != null && number !== "" ? (
            <span className="reservationCardUI__number">{number}</span>
         ) : null}
         {cancelled ? <span className="reservationCardUI__dot" aria-label="Anulată" role="img" /> : null}
         <p className="reservationCardUI__date">
            {date}
            {time ? (
               <>
                  {", "}
                  <span className="reservationCardUI__time">{time}</span>
               </>
            ) : null}
         </p>
         {rows.map((line, i) => (
            <p key={i} className={cx("reservationCardUI__line", i === 0 && rows.length > 1 && "is-sub")}>
               {line}
               {i === rows.length - 1 && flags ? <span className="reservationCardUI__flags">{flags}</span> : null}
            </p>
         ))}
      </article>
   );
}

function ReservationCardList({ children, columns = 2, className = "" }) {
   const [cols, ref] = useGridColumns(columns);
   const items = Children.toArray(children).filter(isValidElement);
   const odd = items.length % cols !== 0;
   // ultima casetă fără pereche ia tot rândul
   const spans = items.map((_, i) => (odd && i === items.length - 1 ? "full" : 1));
   const corners = joinedCorners(spans, cols);
   return (
      <div ref={ref} className={cx("reservationCardUI__list", className)} style={{ "--reservationCardUI-cols": columns }}>
         {items.map((child, i) =>
            cloneElement(child, {
               corners: corners[i],
               className: cx(child.props.className, spans[i] === "full" && "is-full"),
            }),
         )}
      </div>
   );
}

ReservationCardUI.List = ReservationCardList;

export default ReservationCardUI;
